import { useEffect, useState } from 'react'
import { getAsync } from '../api.service'
import { useNearViewport, useOrganizationID, useThumbnailURL } from './use-thumbnail'

// A node's thumbnail when all a page has is the node's id (the inbox's
// course notifications, owner 2026-10-24): content's thumbnail row for that
// node (GET asset-thumbnail/:nodeID/node, the same rows the grid cards read
// in bulk), then the cards' own cache and loader (useThumbnailURL: this
// page's memory, the browser's storage, then storage's stream). The row is
// asked once the element nears the viewport, and an answer (a row, or none)
// is remembered for this tab for five minutes, so a list of fifty
// notifications about three courses asks three times and a thumbnail added
// since is found soon after. A failed lookup is not remembered (the next
// mount asks again) and is "no thumbnail" now: the caller keeps its own
// placeholder.

export const LOOKUP_TTL_MS = 5 * 60 * 1000

const lookups = new Map()

// The newest live row, or null.
const pickRow = rows => {
  const live = (Array.isArray(rows) ? rows : []).filter(r => r && r.id && !r.deletedAt)
  if (!live.length) return null
  return live.reduce((a, b) => (new Date(b.updatedAt || 0) > new Date(a.updatedAt || 0) ? b : a))
}

export const lookupNodeThumbnail = (organizationID, nodeID, now = Date.now()) => {
  const key = `${organizationID || ''}/${nodeID}`
  const held = lookups.get(key)
  if (held && now - held.at < LOOKUP_TTL_MS) return held.promise
  const id = encodeURIComponent(String(nodeID))
  const promise = getAsync(process.env.REACT_APP_API_CONTENT, `api/ddd/asset-thumbnail/${id}/node`)
    .then(response => pickRow(response && response.data))
    .catch(() => {
      if (lookups.get(key) && lookups.get(key).promise === promise) lookups.delete(key)
      return null
    })
  lookups.set(key, { promise, at: now })
  return promise
}

// Tests start clean.
export const resetNodeThumbnailLookups = () => lookups.clear()

// An object URL for the node's thumbnail, or undefined while there is none.
export const useNodeThumbnail = (nodeID, ref) => {
  const organizationID = useOrganizationID()
  const near = useNearViewport(ref)
  const [found, setFound] = useState({ key: '', asset: null })
  const key = nodeID ? `${organizationID || ''}/${nodeID}` : ''

  useEffect(() => {
    if (!near || !nodeID) return undefined
    let live = true
    lookupNodeThumbnail(organizationID, nodeID).then(asset => {
      if (live) setFound({ key: `${organizationID || ''}/${nodeID}`, asset })
    })
    return () => {
      live = false
    }
  }, [near, nodeID, organizationID])

  return useThumbnailURL(found.key === key ? found.asset : null, ref)
}
